import React, { FC, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { UsageBo } from '@utils/types/back-office/usage-bo.ts';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

interface UsageMetricsChartProps {
  data: UsageBo[];
}

type MetricKey =
  | 'stt_units'
  | 'alignment_units'
  | 'translation_characters'
  | 'auto_minute_exports' // Updated key to match UsageBo interface
  | 'custom_models_hosted'
  | 'custom_models_iterations';

interface Metric {
  key: MetricKey;
  name: string;
  color: string;
}

const UsageMetricsChart: FC<UsageMetricsChartProps> = ({ data }) => {
  const [metricType, setMetricType] = useState<MetricKey>('stt_units');
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  /* eslint-disable no-restricted-syntax -- Semantic chart palette per metric */
  const metrics: Metric[] = [
    { key: 'stt_units', name: t('stt-units'), color: '#3b82f6' },
    { key: 'alignment_units', name: t('alignment-units'), color: '#60a5fa' },
    {
      key: 'translation_characters',
      name: t('translation-characters'),
      color: '#93c5fd'
    },
    {
      key: 'auto_minute_exports',
      name: t('stt-templates-exported'),
      color: '#2563eb'
    },
    {
      key: 'custom_models_hosted',
      name: t('custom-models-hosted'),
      color: '#7dd3fc'
    },
    {
      key: 'custom_models_iterations',
      name: t('custom-models-iterations'),
      color: '#1d4ed8'
    }
  ];
  /* eslint-enable no-restricted-syntax */

  // Sort data based on the selected metric.
  // We use a type assertion so that TS knows metricType is a valid key of UsageBo.
  const sortedData = [...data].sort((a, b) => {
    return (
      (b[metricType as keyof UsageBo] as number) -
      (a[metricType as keyof UsageBo] as number)
    );
  });

  const formattedData = sortedData.map(item => {
    const orgName = item.organization_name || '';
    return {
      ...item,
      shortName:
        orgName.length > 15 ? `${orgName.substring(0, 12)}...` : orgName
    };
  });

  const handleMetricChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    setMetricType(event.target.value as MetricKey);
  };

  const selectedMetric = metrics.find(m => m.key === metricType);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h2 className="text-base font-semibold">
          {t('usage-metrics-comparison')}
        </h2>
        <div className="flex items-center gap-x-2">
          <label htmlFor="metricSelect" className="text-xs font-medium">
            {t('metric')}:
          </label>
          <select
            id="metricSelect"
            value={metricType}
            onChange={handleMetricChange}
            className="border border-hairline bg-app-soft rounded-full px-3 py-1.5 text-xs focus:outline-none focus:ring-2 transition-all"
            style={{ color: getColour('normalText') }}
          >
            {metrics.map(metric => (
              <option key={metric.key} value={metric.key}>
                {metric.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%" minHeight={350}>
          <BarChart
            data={formattedData}
            margin={{ top: 10, right: 20, left: 10, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="shortName"
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              label={{
                value: selectedMetric?.name,
                angle: -90,
                position: 'insideLeft',
                offset: 0
              }}
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              formatter={value => {
                if (typeof value === 'number')
                  return [value, selectedMetric?.name];
                return [value?.toString?.(), selectedMetric?.name];
              }}
              labelFormatter={(value, payload) => {
                const item = payload?.find(p => p?.payload?.organization_name);
                return item?.payload?.organization_name || value;
              }}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
            <Bar
              dataKey={metricType}
              fill={selectedMetric?.color}
              name={selectedMetric?.name}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default UsageMetricsChart;
